import type { WalkthroughPath, WalkthroughPathId, WalkthroughStep } from './types';

const PROJECT = '/projects/alpha';

// Money-path walkthrough — Tim user-journeys §9.2. Gated behind `?demo=true`
// or `?demo=money-path` (see chrome/Walkthrough.tsx). Every hash carries `wt=`
// so pathname-only matching cannot collapse steps that share /overview or /advisory.
export const MONEY_PATH: WalkthroughPath = {
  id: 'money-path',
  title: 'Money path',
  steps: [
    {
      hash: `${PROJECT}/advisory?demo=true&tab=alarms&wt=1`,
      persona: 'Control room',
      caption: '06:42 DCS alarm ZN-PH-01 — ack; zero-touch lime SP',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=true&tab=alarms&wt=1` },
      ],
    },
    {
      hash: `${PROJECT}/advisory?demo=true&tab=queue&wt=2`,
      persona: 'Control room',
      caption: 'Morning — ZN-REC opens at 91% confidence',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=true&tab=queue&wt=2` },
      ],
      drawer: { kind: 'case-drawer', data: { caseId: 'case-zn-recovery' } },
    },
    {
      hash: `${PROJECT}/overview?demo=true&wt=3`,
      persona: 'Operator',
      caption: 'Ask branch — SMBS dosing check on My Shift',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Overview', hash: `${PROJECT}/overview?demo=true&wt=3` },
      ],
    },
    {
      hash: `${PROJECT}/process?demo=true&wt=4`,
      persona: 'Control room',
      caption: 'Ask branch — confirm lime loop AIC5002 AUTO',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Process', hash: `${PROJECT}/process?demo=true&wt=4` },
      ],
    },
    {
      hash: `${PROJECT}/compliance?demo=true&tab=shift&wt=5`,
      persona: 'Ops supervisor',
      caption: '10:00 Zn Rougher Conc MISS — confidence discounts',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Compliance', hash: `${PROJECT}/compliance?demo=true&tab=shift&wt=5` },
      ],
    },
    {
      hash: `${PROJECT}/advisory?demo=true&tab=queue&wt=6`,
      persona: 'Metallurgist',
      caption: 'Met reviews ZN-REC, approves actions, closes outcome',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=true&tab=queue&wt=6` },
      ],
      drawer: { kind: 'case-drawer', data: { caseId: 'case-zn-recovery' } },
    },
    {
      hash: `${PROJECT}/advisory?demo=true&tab=approvals&wt=7`,
      persona: 'GM',
      caption: 'GM gate — proposed FMEA row pending Plant Manager approval',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=true&tab=approvals&wt=7` },
      ],
    },
    {
      hash: `${PROJECT}/overview?demo=true&wt=8`,
      persona: 'Superintendent',
      caption: 'Aggregate — plant queue / production triad',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Overview', hash: `${PROJECT}/overview?demo=true&wt=8` },
      ],
    },
    {
      hash: `${PROJECT}/overview?demo=true&wt=9`,
      persona: 'GM',
      caption: 'Aggregate — morning KPI scan, Δ vs Design',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Overview', hash: `${PROJECT}/overview?demo=true&wt=9` },
      ],
    },
  ],
};

// Learning loop — paper → control plan / TARP → rule set → case → review → approval → standard.
export const LEARNING_LOOP: WalkthroughPath = {
  id: 'learning-loop',
  title: 'Learning loop',
  steps: [
    {
      hash: `${PROJECT}/ingestion?demo=learning-loop&wt=1`,
      persona: 'Metallurgist',
      caption:
        'Antimony and bismuth literature review — one proposed row enters the rule set.',
      scrollTo: 'walkthrough-ingestion-loop-input',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Ingestion', hash: `${PROJECT}/ingestion?demo=learning-loop&wt=1` },
      ],
    },
    {
      hash: `${PROJECT}/advisory?demo=learning-loop&tab=rule-set&cause=MD-054&wt=2`,
      persona: 'Metallurgist',
      caption:
        'The proposed row carries literature provenance — proposed, not yet live.',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        {
          label: 'Advisory',
          hash: `${PROJECT}/advisory?demo=learning-loop&tab=rule-set&cause=MD-054&wt=2`,
        },
      ],
    },
    {
      hash: `${PROJECT}/advisory?demo=learning-loop&tab=rule-set&cause=MD-021&wt=3`,
      persona: 'Metallurgist',
      caption:
        'The pH rule carries the control-plan band, the workbook cause, and the TARP escalation.',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        {
          label: 'Advisory',
          hash: `${PROJECT}/advisory?demo=learning-loop&tab=rule-set&cause=MD-021&wt=3`,
        },
      ],
    },
    {
      hash: `${PROJECT}/advisory?demo=learning-loop&tab=queue&wt=4`,
      persona: 'Metallurgist',
      caption:
        'ZN-REC sits on tier 2 of the pH ladder — the TARP instruction reaches the metallurgist.',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=learning-loop&tab=queue&wt=4` },
      ],
      drawer: {
        kind: 'case-drawer',
        data: {
          caseId: 'case-zn-recovery',
          section: 'walkthrough-escalation-ladder',
        },
      },
    },
    {
      hash: `${PROJECT}/advisory?demo=learning-loop&tab=approvals&review=FMR-0007&wt=5`,
      persona: 'Metallurgist',
      caption: 'The pH rule change is reviewed — the GM holds the final gate.',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        {
          label: 'Advisory',
          hash: `${PROJECT}/advisory?demo=learning-loop&tab=approvals&review=FMR-0007&wt=5`,
        },
      ],
    },
    {
      hash: `${PROJECT}/advisory?demo=learning-loop&tab=approvals&review=FMR-0018&wt=6`,
      persona: 'GM',
      caption: 'A zone change proposes a new recovery band — approval makes it the standard.',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        {
          label: 'Advisory',
          hash: `${PROJECT}/advisory?demo=learning-loop&tab=approvals&review=FMR-0018&wt=6`,
        },
      ],
    },
    {
      hash: `${PROJECT}/advisory?demo=learning-loop&tab=rule-set&wt=7`,
      persona: 'GM',
      caption: 'The rule set reads the version in force and the band the plant runs today.',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        {
          label: 'Advisory',
          hash: `${PROJECT}/advisory?demo=learning-loop&tab=rule-set&wt=7`,
        },
      ],
    },
  ],
};

// Komatsu path (W4, #568) — the four beats as the run sheet's rows in order:
// value band → replay → zinc case → action gate → living FMEA → capacity ladder
// → grade what-if → strategy → downtime → bearing handoff → stop approval → the
// GM landing's decision rail. Gated behind `?demo=komatsu`.
// Captions are the run sheet's cue for each beat, not a second copy of the
// glass: no figure is typed into a caption (the glass carries the numbers).
export const KOMATSU_PATH: WalkthroughPath = {
  id: 'komatsu',
  title: 'Komatsu — the four beats',
  steps: [
    {
      hash: `${PROJECT}/advisory?demo=komatsu&wt=1`,
      persona: 'GM',
      caption:
        'Beat 1 · What the plant leaves on the table — Recovery, Throughput, Uptime in US$/yr',
      scrollTo: 'walkthrough-value-band',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=komatsu&wt=1` },
      ],
    },
    {
      hash: `${PROJECT}/advisory?demo=komatsu&wt=2`,
      persona: 'GM',
      caption: 'Beat 2 · Press play on the replay — plant data to case to value, stage by stage',
      scrollTo: 'walkthrough-flow-replay',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=komatsu&wt=2` },
      ],
    },
    {
      hash: `${PROJECT}/advisory/case/case-zn-rgh-ph?demo=komatsu&wt=3`,
      persona: 'Metallurgist',
      caption:
        "Beat 2 · The zinc case page — evidence, routing, then the agent investigation's Run",
      scrollTo: 'walkthrough-case-columns',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=komatsu&wt=3` },
        { label: 'Case', hash: `${PROJECT}/advisory/case/case-zn-rgh-ph?demo=komatsu&wt=3` },
      ],
    },
    {
      hash: `${PROJECT}/advisory/case/case-zn-rgh-ph?demo=komatsu&wt=4`,
      persona: 'Metallurgist',
      caption: 'Beat 2 · Control Metallurgist approves the action at the Action gate',
      scrollTo: 'walkthrough-case-action-gate',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=komatsu&wt=4` },
        { label: 'Case', hash: `${PROJECT}/advisory/case/case-zn-rgh-ph?demo=komatsu&wt=4` },
      ],
    },
    {
      hash: `${PROJECT}/advisory/case/case-zn-rgh-ph?demo=komatsu&wt=5`,
      persona: 'GM',
      caption: 'Beat 3 · Senior-met seat approves the FMEA proposal; the living FMEA lights up',
      scrollTo: 'walkthrough-living-fmea',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=komatsu&wt=5` },
        { label: 'Case', hash: `${PROJECT}/advisory/case/case-zn-rgh-ph?demo=komatsu&wt=5` },
      ],
    },
    {
      hash: `${PROJECT}/capacity?demo=komatsu&wt=6`,
      persona: 'Metallurgist',
      caption:
        'Beat 4a · The capacity ladder — grinding first, then the next constraint and its worth',
      scrollTo: 'walkthrough-capacity-ladder',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Capacity & strategy', hash: `${PROJECT}/capacity?demo=komatsu&wt=6` },
      ],
    },
    {
      hash: `${PROJECT}/capacity?demo=komatsu&wt=7`,
      persona: 'Metallurgist',
      caption:
        'Beat 4a · Drag the grade to Zn 11 % — feed capped by the named circuit, capital priced',
      scrollTo: 'walkthrough-capacity-grade',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Capacity & strategy', hash: `${PROJECT}/capacity?demo=komatsu&wt=7` },
      ],
    },
    {
      hash: `${PROJECT}/capacity?tab=1&demo=komatsu&wt=8`,
      persona: 'Metallurgist',
      caption: 'Beat 4a · Strategy — move the silver slider and watch the recommendation flip',
      scrollTo: 'walkthrough-capacity-strategy',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Capacity & strategy', hash: `${PROJECT}/capacity?tab=1&demo=komatsu&wt=8` },
      ],
    },
    {
      hash: `${PROJECT}/downtime?demo=komatsu&wt=9`,
      persona: 'Maintenance',
      caption: 'Beat 4b · Uptime — hours, tonnes and US$ before any tab; causes ranked by US$',
      scrollTo: 'walkthrough-downtime-impact',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Downtime', hash: `${PROJECT}/downtime?demo=komatsu&wt=9` },
      ],
    },
    {
      hash: `${PROJECT}/advisory?demo=komatsu&wt=10`,
      persona: 'Maintenance',
      caption: 'Beat 4c · Your equipment — the ball-mill bearing case arrives on the flow',
      scrollTo: 'walkthrough-flow-strip',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=komatsu&wt=10` },
      ],
    },
    {
      hash: `${PROJECT}/advisory/case/case-bm-brg?demo=komatsu&wt=11`,
      persona: 'Maintenance',
      caption: 'Beat 4c · Maintenance approves the stop — work order WO-441, escalated to the GM',
      scrollTo: 'walkthrough-bearing-stop',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Advisory', hash: `${PROJECT}/advisory?demo=komatsu&wt=11` },
        { label: 'Case', hash: `${PROJECT}/advisory/case/case-bm-brg?demo=komatsu&wt=11` },
      ],
    },
    {
      hash: `${PROJECT}/overview?demo=komatsu&wt=12`,
      persona: 'GM',
      caption: "Beat 4c · The manager's landing — open Escalations and record the stop",
      scrollTo: 'walkthrough-gm-lead',
      breadcrumb: [
        { label: 'Lead-zinc concentrator', hash: PROJECT },
        { label: 'Overview', hash: `${PROJECT}/overview?demo=komatsu&wt=12` },
      ],
    },
  ],
};

export const WALKTHROUGH_PATHS: Record<WalkthroughPathId, WalkthroughPath> = {
  'money-path': MONEY_PATH,
  'learning-loop': LEARNING_LOOP,
  komatsu: KOMATSU_PATH,
};

export const WALKTHROUGH_PATH_LIST: readonly WalkthroughPath[] = [
  MONEY_PATH,
  LEARNING_LOOP,
  KOMATSU_PATH,
];

/** @deprecated use MONEY_PATH.steps or WALKTHROUGH_PATHS[id].steps */
export const STEPS: WalkthroughStep[] = MONEY_PATH.steps;

export function stepsForPath(pathId: WalkthroughPathId): readonly WalkthroughStep[] {
  return WALKTHROUGH_PATHS[pathId].steps;
}

// Resolve a path+search to a step. Exact match only — never pathname-only
// when multiple steps share the same route (wt= disambiguates).
export function findStepByHash(
  hash: string,
): { path: WalkthroughPath; index: number } | null {
  for (const path of WALKTHROUGH_PATH_LIST) {
    const index = path.steps.findIndex((s) => s.hash === hash);
    if (index !== -1) return { path, index };
  }
  return null;
}
